import { useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '@/lib/auth/store';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { USER_ROLES } from '@/lib/constants/roles';
import { usePermission } from '@/lib/permissions/use-permission';
import { useSmsWebhookDetail, useUpdateSmsWebhook } from '../api';
import { parseHeaderJson } from '../payload';
import type { ApiBuilderState } from '../components/ApiBuilder';
import {
  SmsWebhookForm,
  type SmsRequestType,
  type SmsWebhookFormValues,
} from './SmsWebhookForm';

function objectToBuilderRows(obj: Record<string, string>) {
  const entries = Object.entries(obj);
  if (entries.length === 0) return [{ key: '', value: '' }];
  return entries.map(([key, value]) => ({ key, value }));
}

export function UpdateSmsWebhookPage() {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  const isSubOrSuper = useAuthStore((s) => s.isSubOrSuperAdmin());
  const canEditAcl = usePermission('Manage Campaign.SMS Webhook List', BASE_PERMISSION.EDIT);
  const canEdit = role !== USER_ROLES.S_ADMIN && canEditAcl;
  const { id: idParam } = useParams<{ id: string }>();
  const webhookId = Number(idParam);

  const detail = useSmsWebhookDetail(webhookId);
  const update = useUpdateSmsWebhook();

  const initial = useMemo<SmsWebhookFormValues | null>(() => {
    if (!detail.data) return null;
    const headerObj = parseHeaderJson(detail.data.header);
    const method = (detail.data.requestType as SmsRequestType) || 'GET';

    // Best-effort hydrate of the body editor — try to parse the saved
    // payload as JSON so the user sees rows; otherwise stash it as raw.
    let bodyRows = [{ key: '', value: '' }];
    let bodyMode: 'key-value' | 'raw' = 'key-value';
    let rawBody = '';
    if (method === 'POST' && detail.data.payload) {
      try {
        const parsed = JSON.parse(detail.data.payload) as Record<string, unknown>;
        bodyRows = Object.entries(parsed).map(([key, v]) => ({
          key,
          value:
            v && typeof v === 'object' && !Array.isArray(v)
              ? JSON.stringify(v)
              : String(v),
        }));
        rawBody = JSON.stringify(parsed, null, 2);
      } catch {
        bodyMode = 'raw';
        rawBody = detail.data.payload;
      }
    }

    const builder: ApiBuilderState = {
      url: detail.data.url ?? '',
      method,
      headers: objectToBuilderRows(headerObj),
      body: bodyRows,
      rawHeaders: detail.data.header ?? '',
      rawBody,
      headerMode: 'key-value',
      bodyMode,
    };

    return {
      webhookName: detail.data.webhookName ?? '',
      smsText: detail.data.smsText ?? '',
      payload: detail.data.payload ?? '',
      apiBuilder: builder,
    };
  }, [detail.data]);

  // Super-admin still can't edit SMS webhooks (unchanged); sub-super-admin
  // now needs the explicit ACL "edit" permission on SMS Webhook List instead
  // of a blanket block.
  if (isSubOrSuper && !canEdit) return <Navigate to="/sms-webhook-list" replace />;

  if (!initial) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading webhook…
      </div>
    );
  }

  return (
    <SmsWebhookForm
      mode="update"
      initialValues={initial}
      isPending={update.isPending}
      onSubmit={(payload) => {
        update.mutate(
          {
            ...payload,
            id: webhookId,
            userId: Number(userId),
          },
          {
            onSuccess: (res) => {
              if (res?.message === 'Parameters Incorrect') {
                toast.error(res.message);
                return;
              }
              toast.success(res?.message ?? 'SMS webhook updated');
              navigate('/sms-webhook-list');
            },
            onError: () => toast.error('Could not update the SMS webhook.'),
          },
        );
      }}
    />
  );
}
